'use client'

import { type EBorrowCategory } from '@/config/euler-contracts'
import { E_BORROW_CATEGORIES } from '@/config/market-config'
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/ui/atoms/dialog/Dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/ui/atoms/dropdown/DropdownMenu'
import { MobileTokenFilter } from '@/ui/molecules/token-filter/MobileTokenFilter'
import { TokenFilterDialog } from '@/ui/molecules/token-filter/TokenFilterDialog'
import { cn } from '@/ui/utils/style'
import { ExpandableSearch } from '@/ui/atoms/expandable-search/ExpandableSearch'
import { ArrowDownWideNarrow, ArrowUpNarrowWide, SlidersHorizontal, TrendingUp, Wallet } from 'lucide-react'
import type { ReactNode } from 'react'

export type BorrowSortField = 'rank' | 'apy' | 'liquidity' | 'utilization' | 'ltv'
export type BorrowSortDirection = 'asc' | 'desc'

export interface EBorrowFilter {
  categories: EBorrowCategory[]
  searchQuery: string
  collateralFilter: string[]
  debtFilter: string[]
  inWalletOnly: boolean
  openPositionsOnly: boolean
  sortBy: BorrowSortField
  sortDirection: BorrowSortDirection
}

function getSortFields(showLeverageMetrics: boolean): { value: BorrowSortField; label: string }[] {
  return [
    { value: 'rank', label: 'Best' },
    { value: 'apy', label: showLeverageMetrics ? 'ROE' : 'APY' },
    { value: 'liquidity', label: 'Liquidity' },
    { value: 'ltv', label: 'LTV' },
    { value: 'utilization', label: 'Utilization' },
  ]
}

interface EBorrowFilterBarProps {
  filter: EBorrowFilter
  onFilterChange: (filter: EBorrowFilter) => void
  hasWallet: boolean
  showLeverageMetrics?: boolean
  onShowLeverageMetricsChange?: (show: boolean) => void
  collateralOptions?: string[]
  debtOptions?: string[]
  collateralAddressBySymbol?: Record<string, string>
  debtAddressBySymbol?: Record<string, string>
}

export function EBorrowFilterBar({
  filter,
  onFilterChange,
  hasWallet,
  showLeverageMetrics = false,
  onShowLeverageMetricsChange,
  collateralOptions = [],
  debtOptions = [],
  collateralAddressBySymbol,
  debtAddressBySymbol,
}: EBorrowFilterBarProps) {
  const handleCategoryChange = (category: EBorrowCategory) => {
    const current = filter.categories ?? []
    if (category === 'All') {
      onFilterChange({ ...filter, categories: [] })
      return
    }
    const isSelected = current.includes(category)
    const updated = isSelected ? current.filter((c) => c !== category) : [...current, category]
    onFilterChange({ ...filter, categories: updated })
  }

  const categories = filter.categories ?? []
  const activeFilterCount =
    categories.length +
    (filter.inWalletOnly ? 1 : 0) +
    (filter.openPositionsOnly ? 1 : 0) +
    (filter.collateralFilter.length > 0 ? 1 : 0) +
    (filter.debtFilter.length > 0 ? 1 : 0)

  const isAllSelected = categories.length === 0

  return (
    <div className="sticky top-12 z-10 flex flex-wrap items-center justify-between gap-2 bg-background py-2 lg:static lg:gap-4 lg:bg-transparent lg:py-0">
      <div className="hidden items-center gap-2 lg:flex lg:flex-wrap">
        <FilterPill selected={isAllSelected} onClick={() => onFilterChange({ ...filter, categories: [] })}>
          All
        </FilterPill>
        <div className="mx-1 h-4 w-px shrink-0 bg-white/10" />
        {E_BORROW_CATEGORIES.filter((c) => c !== 'All').map((cat) => (
          <FilterPill key={cat} selected={categories.includes(cat)} onClick={() => handleCategoryChange(cat)}>
            {cat}
          </FilterPill>
        ))}
        <div className="mx-1 h-4 w-px bg-white/10" />
        {collateralOptions.length > 0 && (
          <TokenFilterDialog
            options={collateralOptions}
            selected={filter.collateralFilter}
            onSelect={(symbols) => onFilterChange({ ...filter, collateralFilter: symbols })}
            title="Collateral"
            tokenAddressBySymbol={collateralAddressBySymbol}
          />
        )}
        {debtOptions.length > 0 && (
          <TokenFilterDialog
            options={debtOptions}
            selected={filter.debtFilter}
            onSelect={(symbols) => onFilterChange({ ...filter, debtFilter: symbols })}
            title="Debt"
            tokenAddressBySymbol={debtAddressBySymbol}
          />
        )}
      </div>

      <div className="lg:hidden">
        <Dialog>
          <DialogTrigger asChild>
            <button
              type="button"
              className={cn(
                'relative flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 font-medium text-sm transition-colors',
                activeFilterCount > 0
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-white/5 text-muted-foreground hover:bg-white/10 hover:text-foreground',
              )}
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
              <span>Filters</span>
              {activeFilterCount > 0 && (
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-white/20 text-xs">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </DialogTrigger>
          <DialogContent className="p-4 pt-16 sm:p-6" aria-describedby={undefined}>
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-2 pr-8">
                <DialogTitle className="text-lg">Filters</DialogTitle>
              </div>
              <div className="space-y-2">
                <div className="font-medium text-muted-foreground text-xs uppercase tracking-wider">Category</div>
                <div className="flex flex-wrap gap-1.5">
                  {E_BORROW_CATEGORIES.filter((c) => c !== 'All').map((cat) => (
                    <FilterPill
                      key={cat}
                      selected={categories.includes(cat)}
                      onClick={() => handleCategoryChange(cat)}
                      size="sm"
                    >
                      {cat}
                    </FilterPill>
                  ))}
                </div>
              </div>
              {collateralOptions.length > 0 && (
                <div className="space-y-2">
                  <div className="font-medium text-muted-foreground text-xs uppercase tracking-wider">Collateral</div>
                  <MobileTokenFilter
                    options={collateralOptions}
                    selected={filter.collateralFilter}
                    onSelect={(symbols) => onFilterChange({ ...filter, collateralFilter: symbols })}
                    tokenAddressBySymbol={collateralAddressBySymbol}
                  />
                </div>
              )}
              {debtOptions.length > 0 && (
                <div className="space-y-2">
                  <div className="font-medium text-muted-foreground text-xs uppercase tracking-wider">Debt</div>
                  <MobileTokenFilter
                    options={debtOptions}
                    selected={filter.debtFilter}
                    onSelect={(symbols) => onFilterChange({ ...filter, debtFilter: symbols })}
                    tokenAddressBySymbol={debtAddressBySymbol}
                  />
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="ml-auto flex items-center gap-2">
        <ExpandableSearch
          value={filter.searchQuery}
          onChange={(query) => onFilterChange({ ...filter, searchQuery: query })}
        />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex h-9 items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-2.5 text-muted-foreground transition-colors lg:hidden hover:text-foreground"
              title="Sort options"
            >
              {filter.sortDirection === 'desc' ? (
                <ArrowDownWideNarrow className="h-4 w-4" />
              ) : (
                <ArrowUpNarrowWide className="h-4 w-4" />
              )}
              <span className="font-medium text-xs">
                {getSortFields(showLeverageMetrics).find((f) => f.value === filter.sortBy)?.label || 'Sort'}
              </span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-36 p-2">
            <div className="flex flex-col gap-2">
              <div className="font-medium text-muted-foreground text-xs uppercase tracking-wider">Sort by</div>
              <div className="flex flex-wrap gap-1.5">
                {getSortFields(showLeverageMetrics).map((field) => (
                  <button
                    key={field.value}
                    type="button"
                    onClick={() => {
                      if (filter.sortBy === field.value) {
                        // Don't toggle direction for 'rank' (Best) since it has no effect
                        if (field.value === 'rank') return
                        onFilterChange({
                          ...filter,
                          sortDirection: filter.sortDirection === 'desc' ? 'asc' : 'desc',
                        })
                      } else {
                        onFilterChange({ ...filter, sortBy: field.value })
                      }
                    }}
                    className={cn(
                      'shrink-0 rounded-full px-2.5 py-1 font-medium text-xs transition-colors',
                      filter.sortBy === field.value
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-white/5 text-muted-foreground hover:bg-white/10 hover:text-foreground',
                    )}
                  >
                    {field.label}
                  </button>
                ))}
              </div>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        {onShowLeverageMetricsChange && (
          <button
            type="button"
            onClick={() => onShowLeverageMetricsChange(!showLeverageMetrics)}
            className={cn(
              'hidden h-9 items-center gap-1.5 rounded-lg border px-2.5 transition-colors lg:flex',
              showLeverageMetrics
                ? 'border-primary/50 bg-primary/10 text-primary'
                : 'border-white/10 bg-white/5 text-muted-foreground hover:text-foreground',
            )}
            title="Show leverage strategies"
            aria-label="Show leverage strategies"
            aria-pressed={showLeverageMetrics}
          >
            <TrendingUp className="h-4 w-4" />
            <span className="font-medium text-xs">Strategies</span>
          </button>
        )}

        {hasWallet && (
          <div className="flex h-9 items-center rounded-lg border border-white/10 bg-white/5">
            <button
              type="button"
              onClick={() =>
                onFilterChange({
                  ...filter,
                  inWalletOnly: !filter.inWalletOnly,
                })
              }
              className={cn(
                'flex h-full items-center justify-center px-2.5 transition-colors',
                filter.inWalletOnly ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
              )}
              title="Show assets in wallet"
              aria-label="Show assets in wallet"
              aria-pressed={filter.inWalletOnly}
            >
              <Wallet className="h-4 w-4" />
            </button>
            <div className="h-4 w-px bg-white/10" />
            <button
              type="button"
              onClick={() =>
                onFilterChange({
                  ...filter,
                  openPositionsOnly: !filter.openPositionsOnly,
                })
              }
              className={cn(
                'group flex h-full items-center justify-center px-2.5 transition-colors',
                filter.openPositionsOnly ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
              )}
              title="Show my positions"
              aria-label="Show my positions"
              aria-pressed={filter.openPositionsOnly}
            >
              <span
                className={cn(
                  'text-sm transition-all',
                  !filter.openPositionsOnly &&
                    'opacity-50 contrast-0 grayscale group-hover:opacity-100 group-hover:brightness-200',
                )}
                style={
                  filter.openPositionsOnly
                    ? {
                        background: 'linear-gradient(90deg, #FCE572 0%, #A1FCE7 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }
                    : undefined
                }
              >
                💰
              </span>
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

interface FilterPillProps {
  children: ReactNode
  selected: boolean
  onClick: () => void
  size?: 'sm' | 'default'
}

function FilterPill({ children, selected, onClick, size = 'default' }: FilterPillProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'shrink-0 rounded-full font-medium transition-colors',
        size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm',
        selected
          ? 'bg-primary text-primary-foreground'
          : 'bg-white/5 text-muted-foreground hover:bg-white/10 hover:text-foreground',
      )}
    >
      {children}
    </button>
  )
}
